import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCreateAiMappingRun, useGetAiMappingRun } from "./hooks";
import { classifyAiMappingError } from "./aiMappingErrors";
import { AiMappingAreaLike, AiMappingRunLike } from "./aiMappingTypes";
import { AiMappingNotify } from "./aiMappingControllerTypes";
import { unwrap } from "./aiMappingControllerUtils";

interface UseAiMappingRunOptions {
  projectMapId: string | null;
  unmountedRef: React.MutableRefObject<boolean>;
  notify: AiMappingNotify;
  handleError: (error: unknown, options?: { inline?: boolean }) => boolean;
  setUnauthorized: (value: boolean) => void;
}

export function useAiMappingRun({
  projectMapId,
  unmountedRef,
  notify: _notify,
  handleError,
  setUnauthorized,
}: UseAiMappingRunOptions) {
  const [run, setRun] = useState<AiMappingRunLike | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const loadAttemptedRef = useRef(false);

  const [createRunAction] = useCreateAiMappingRun();
  const [fetchRun] = useGetAiMappingRun();

  const loadRun = useCallback(async () => {
    if (!projectMapId) return;
    setLoading(true);
    setLoadError(null);
    const result = await createRunAction({ variables: { projectMapId } });
    if (!result.success) {
      if (!unmountedRef.current) {
        const info = classifyAiMappingError(result);
        if (info.code === "unauthorized") {
          setUnauthorized(true);
        } else {
          setLoadError(info.detail ? `${info.message} (${info.detail})` : info.message);
        }
      }
      setLoading(false);
      return;
    }
    const created = result.data?.createAiMappingRun ?? null;
    if (!unmountedRef.current && created) {
      setRun(created as AiMappingRunLike);
    }
    if (!unmountedRef.current) setLoading(false);
  }, [projectMapId, createRunAction, unmountedRef, setUnauthorized]);

  const retryLoadRun = useCallback(() => {
    loadAttemptedRef.current = true;
    loadRun();
  }, [loadRun]);

  const reset = useCallback(() => {
    loadAttemptedRef.current = false;
    setRun(null);
    setLoadError(null);
    setUnauthorized(false);
  }, [setUnauthorized]);

  const refreshRun = useCallback(async () => {
    if (!projectMapId) return;
    try {
      const refreshed = unwrap(
        await fetchRun({ variables: { projectMapId }, fetchPolicy: "network-only" }),
        (data) => data?.getAiMappingRun
      );
      if (!unmountedRef.current && refreshed) {
        setRun(refreshed as AiMappingRunLike);
      }
    } catch (error) {
      if (!unmountedRef.current) handleError(error);
    }
  }, [projectMapId, fetchRun, handleError, unmountedRef]);

  const updateAreaInRun = useCallback((area: AiMappingAreaLike) => {
    setRun((current) => {
      if (!current) return current;
      const exists = current.areas.some((existing) => existing.id === area.id);
      return {
        ...current,
        areas: exists
          ? current.areas.map((existing) => (existing.id === area.id ? area : existing))
          : [...current.areas, area],
      };
    });
  }, []);

  const getAreaById = useCallback(
    (areaId: string): AiMappingAreaLike | null =>
      run?.areas.find((area) => area.id === areaId) ?? null,
    [run]
  );

  const areas = useMemo(() => run?.areas ?? [], [run]);

  return {
    run,
    setRun,
    loading,
    loadError,
    areas,
    loadAttemptedRef,
    loadRun,
    retryLoadRun,
    reset,
    refreshRun,
    updateAreaInRun,
    getAreaById,
  };
}
